import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DiffEditorModel } from 'ngx-monaco-editor-v2';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import beautify from 'xml-beautifier';
import { ToastrService } from 'ngx-toastr';
import { CommonService } from '../services/common.service';
import { RuleEngineService } from '../services/rule-engine.service';
import { W700servicesService } from '../services/w700services.service';

export type CompareSectionKey =
  | 'transform'
  | 'validation'
  | 'delivery'
  | 'rule'
  | 'vaRules'
  | 'lookups';
export type CompareEditorKey = 'transform' | 'validation' | 'delivery' | 'rule' | 'vaRules';

/** Legacy validationconfig XML vs RouteRuleMapping / RuleDefinition (PreValidation). */
export type ValidationMappingMode = 'legacy' | 'rules' | 'none';

export type RuleExecutionStage = 'Delivered' | 'Validation' | 'PreValidation';

/** Metadata from GetMappings/Get for SaveTransformConfig / SaveValidationConfig. */
export interface CompareMappingSaveMeta {
  template: string;
  resultjson: string;
  version: string;
  coreConfigVersionList: any[];
  liveVersion: string;
}

export interface MappingLoadResult {
  displayXml: string;
  rawXml: string;
  meta: CompareMappingSaveMeta;
  validationMode?: ValidationMappingMode;
}

interface MappingLoadError {
  error: string;
}

export type ComparePaneSide = 'left' | 'right';

interface XmlMappingSaveResult {
  side: ComparePaneSide;
  message: string;
  meta: CompareMappingSaveMeta;
}

type ValidationSaveResult =
  | XmlMappingSaveResult
  | { side: ComparePaneSide; message: string; refreshedContent?: string };

export interface CompareLookupRow {
  id: number;
  contextId: string;
  lookupType: string;
  lookupId: string;
  lookupValue: string;
  selected: boolean;
}

export interface CompareSectionState {
  expanded: boolean;
  loaded: boolean;
  loading: boolean;
  editing: boolean;
  saving: boolean;
  showEditor: boolean;
  error: string | null;
  leftContent: string;
  rightContent: string;
  leftContentRaw: string;
  rightContentRaw: string;
  /** Snapshot at load / edit start for change detection on save. */
  leftBackup: string;
  rightBackup: string;
  leftSaveMeta: CompareMappingSaveMeta | null;
  rightSaveMeta: CompareMappingSaveMeta | null;
  leftLookups: CompareLookupRow[];
  rightLookups: CompareLookupRow[];
  /** @deprecated Both sides may be selected; kept for clear/reset compatibility. */
  lookupSelectionSide: ComparePaneSide | null;
  lookupSyncing: boolean;
  /** Per-pane validation source: legacy XML mapping or pre-validation rules JSON. */
  leftValidationMode: ValidationMappingMode;
  rightValidationMode: ValidationMappingMode;
}

@Component({
  selector: 'app-interface-compare',
  templateUrl: './interface-compare.component.html',
  styleUrls: ['./interface-compare.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterfaceCompareComponent implements OnInit {
  @Input() inputDataInModalComponent: { left: any; right: any };

  leftInterface: any;
  rightInterface: any;

  sections: Record<CompareSectionKey, CompareSectionState> = {
    transform: this.createSectionState(false),
    validation: this.createSectionState(false),
    delivery: this.createSectionState(false),
    rule: this.createSectionState(false),
    vaRules: this.createSectionState(false),
    lookups: this.createSectionState(false),
  };

  readonly lookupHelpTooltip =
    'Insert: select row(s) on one side only, then use the arrow toward the other side.\n\n' +
    'Update: select the same number of rows on both sides (selected rows stay at the top; 1st↔1st, 2nd↔2nd…). Review the pairs, then confirm.\n\n' +
    'Rows are unique by Lookup Type + Lookup Id + Lookup Value — duplicates are skipped.';

  /** Pending position-paired update awaiting user confirm. */
  lookupUpdatePreview: {
    sourceSide: ComparePaneSide;
    targetSide: ComparePaneSide;
    pairs: Array<{ source: CompareLookupRow; target: CompareLookupRow }>;
  } | null = null;

  transformOldModel: DiffEditorModel = { code: '', language: 'xml' };
  transformNewModel: DiffEditorModel = { code: '', language: 'xml' };
  validationOldModel: DiffEditorModel = { code: '', language: 'xml' };
  validationNewModel: DiffEditorModel = { code: '', language: 'xml' };
  deliveryOldModel: DiffEditorModel = { code: '', language: 'json' };
  deliveryNewModel: DiffEditorModel = { code: '', language: 'json' };
  ruleOldModel: DiffEditorModel = { code: '', language: 'json' };
  ruleNewModel: DiffEditorModel = { code: '', language: 'json' };
  vaRulesOldModel: DiffEditorModel = { code: '', language: 'json' };
  vaRulesNewModel: DiffEditorModel = { code: '', language: 'json' };

  private diffEditors: Partial<Record<CompareEditorKey, any>> = {};

  private readonly monacoPerfOptions = {
    minimap: { enabled: false },
    renderOverviewRuler: false,
    automaticLayout: true,
  };

  transformDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'xml',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  validationDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'xml',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  deliveryDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'json',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  ruleDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'json',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  vaRulesDiffOptions: Record<string, unknown> = {
    theme: 'vs',
    language: 'json',
    readOnly: true,
    originalEditable: false,
    lineNumbers: true,
    wordWrap: 'on',
    renderSideBySide: true,
    scrollBeyondLastLine: false,
    ...this.monacoPerfOptions,
  };

  constructor(
    private activeModal: NgbActiveModal,
    private service: CommonService,
    private ruleEngineService: RuleEngineService,
    private w700Service: W700servicesService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.inputDataInModalComponent?.left || !this.inputDataInModalComponent?.right) {
      this.toastr.error('Compare data is missing.', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right',
      });
      this.close();
      return;
    }
    this.leftInterface = this.inputDataInModalComponent.left;
    this.rightInterface = this.inputDataInModalComponent.right;
  }

  /** Resolves a display field from list-row properties (casing varies). */
  displayField(iface: any, keys: string[]): string {
    if (!iface) {
      return '—';
    }
    for (const key of keys) {
      const val = iface[key];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return String(val);
      }
    }
    return '—';
  }

  /** Matches interface list: active_card vs failed_card (status === 'active'). */
  isInterfaceActive(iface: any): boolean {
    return String(iface?.status ?? '').toLowerCase() === 'active';
  }

  private createSectionState(expanded: boolean): CompareSectionState {
    return {
      expanded,
      loaded: false,
      loading: false,
      editing: false,
      saving: false,
      showEditor: false,
      error: null,
      leftContent: '',
      rightContent: '',
      leftContentRaw: '',
      rightContentRaw: '',
      leftBackup: '',
      rightBackup: '',
      leftSaveMeta: null,
      rightSaveMeta: null,
      leftLookups: [],
      rightLookups: [],
      lookupSelectionSide: null,
      lookupSyncing: false,
      leftValidationMode: 'legacy',
      rightValidationMode: 'legacy',
    };
  }

  onDiffEditorInit(key: CompareEditorKey, editor: any): void {
    this.diffEditors[key] = editor;
  }

  isEditableSection(key: CompareSectionKey): boolean {
    return key !== 'lookups' && key !== 'vaRules';
  }

  hasDiffEditor(key: CompareSectionKey): boolean {
    return key !== 'lookups';
  }

  canEditSection(key: CompareSectionKey): boolean {
    const section = this.sections[key];
    if (!this.isEditableSection(key) || !section.loaded || section.loading) {
      return false;
    }
    return !this.isInterfaceLocked(this.leftInterface) || !this.isInterfaceLocked(this.rightInterface);
  }

  isInterfaceLocked(iface: any): boolean {
    const raw = iface?.IsLocked ?? iface?.isLocked ?? false;
    return String(raw).toLowerCase() === 'true' || raw === true;
  }

  isSideEditable(side: ComparePaneSide): boolean {
    const iface = side === 'left' ? this.leftInterface : this.rightInterface;
    return !this.isInterfaceLocked(iface);
  }

  /** Hide Rule + VA rules when both compared interfaces are inbound. */
  showRuleCompareSections(): boolean {
    return !(
      this.isInboundInterface(this.leftInterface) &&
      this.isInboundInterface(this.rightInterface)
    );
  }

  private isInboundInterface(iface: any): boolean {
    if (!iface) {
      return false;
    }
    const direction = String(iface?.Direction ?? iface?.direction ?? '').toLowerCase();
    const interfaceType = String(
      iface?.interfacetype ?? iface?.INTERFACETYPE ?? '',
    ).toLowerCase();
    return direction.includes('inbound') || interfaceType.includes('inbound');
  }

  /** Edit is only available while the section is expanded (and not already editing). */
  isEditDisabled(key: CompareSectionKey): boolean {
    const section = this.sections[key];
    return (
      !section.expanded ||
      !section.loaded ||
      section.loading ||
      section.editing ||
      section.saving ||
      !this.canEditSection(key)
    );
  }

  isCancelEditDisabled(key: CompareEditorKey): boolean {
    const section = this.sections[key];
    return !section.editing || section.saving;
  }

  private getSectionDisplayName(key: CompareSectionKey): string {
    switch (key) {
      case 'transform':
        return 'Transformation mapping';
      case 'validation':
        return 'Validation mapping';
      case 'delivery':
        return 'Pre-Delivery rules';
      case 'rule':
        return 'Rule';
      case 'vaRules':
        return 'VA rules';
      default:
        return 'Section';
    }
  }

  private getSaveSuccessMessage(side: ComparePaneSide, key: CompareSectionKey): string {
    const sideLabel = side === 'left' ? 'Left' : 'Right';
    return `${sideLabel} ${this.getSectionDisplayName(key)} saved successfully`;
  }

  private showSaveSuccessToasts(results: { side: ComparePaneSide }[], key: CompareSectionKey): void {
    results.forEach((r) => {
      this.toastr.success(this.getSaveSuccessMessage(r.side, key), '', {
        timeOut: 2000,
        positionClass: 'toast-bottom-right',
      });
    });
  }

  /** Pipeline audit — contextId is route UniqueID (same as create.component). */
  private recordPipelineSaveAudit(
    uniqueId: string,
    actionType: string,
    newValue: string,
    description: string,
    oldValue = '',
  ): void {
    if (!uniqueId) {
      return;
    }
    this.service
      .auditsave(actionType, oldValue, newValue, 'Pipeline', uniqueId, description)
      .then(() => {})
      .catch(() => {});
  }

  /** Compare save always results in Dev; audit shows version number + Dev only. */
  private formatCompareMappingAuditVersionLabel(
    meta: CompareMappingSaveMeta | null,
    versionToSave: string,
  ): string {
    return `${this.resolveAuditVersionNumber(meta, versionToSave)} Dev`;
  }

  private resolveAuditVersionNumber(
    meta: CompareMappingSaveMeta | null,
    versionToSave: string,
  ): string {
    const trimmed = String(versionToSave ?? '').trim();
    if (trimmed !== '') {
      return trimmed;
    }
    const list = meta?.coreConfigVersionList ?? [];
    let max = 0;
    for (const entry of list) {
      const n = parseInt(String(entry?.version ?? ''), 10);
      if (!isNaN(n) && n > max) {
        max = n;
      }
    }
    return String(max > 0 ? max + 1 : 1);
  }

  private buildMappingSaveAudit(
    meta: CompareMappingSaveMeta | null,
    versionToSave: string,
    key: CompareEditorKey,
    side: ComparePaneSide,
  ): { actionType: string; newValue: string; description: string } {
    const sideLabel = side === 'left' ? 'Left' : 'Right';
    const sectionName = this.getSectionDisplayName(key);
    const versionString = this.formatCompareMappingAuditVersionLabel(meta, versionToSave);
    const isUpdate = String(versionToSave ?? '').trim() !== '';

    if (isUpdate) {
      return {
        actionType: 'Update',
        newValue: `Compare ${sideLabel} ${sectionName}: Mapping version is updated- ${versionString}`,
        description: 'Mapping Version Updated Successfully',
      };
    }
    return {
      actionType: 'Create',
      newValue: `Compare ${sideLabel} ${sectionName}: New Mapping version is Created- ${versionString}`,
      description: 'Mapping Version Created Successfully',
    };
  }

  private buildRuleSaveAudit(
    stage: RuleExecutionStage,
    key: CompareEditorKey,
    side: ComparePaneSide,
    targetVersion: number,
    targetStatus: string,
    isNewRuleConfigVersion: boolean,
  ): { actionType: string; newValue: string; description: string } {
    const sideLabel = side === 'left' ? 'Left' : 'Right';
    const sectionName = this.getSectionDisplayName(key);
    const auditLabel = this.resolveRuleAuditLabel(stage);
    const prefix = `Compare ${sideLabel} ${sectionName}: `;

    if (isNewRuleConfigVersion) {
      return {
        actionType: 'Create',
        newValue: `${prefix}New ${auditLabel} version is Created- ${targetVersion} ${targetStatus}`,
        description: `${auditLabel} Version Created Successfully.`,
      };
    }
    return {
      actionType: 'Update',
      newValue: `${prefix}${auditLabel} version is updated- ${targetVersion} ${targetStatus}`,
      description: `${auditLabel} Version Updated Successfully`,
    };
  }

  getSectionLockNote(key: CompareSectionKey): string | null {
    const leftLocked = this.isInterfaceLocked(this.leftInterface);
    const rightLocked = this.isInterfaceLocked(this.rightInterface);
    if (leftLocked && rightLocked) {
      return 'Both interfaces are locked. Editing is disabled.';
    }
    if (leftLocked) {
      return 'Left interface is locked. Left pane is read-only.';
    }
    if (rightLocked) {
      return 'Right interface is locked. Right pane is read-only.';
    }
    return null;
  }

  /** Notes when left/right validation use different storage (legacy XML vs pre-validation rules). */
  getValidationFormatNote(): string | null {
    const section = this.sections.validation;
    if (!section.loaded) {
      return null;
    }
    const leftMode = section.leftValidationMode;
    const rightMode = section.rightValidationMode;
    if (leftMode === 'none' && rightMode === 'none') {
      return null;
    }
    const leftLabel = this.getValidationModeLabel(leftMode);
    const rightLabel = this.getValidationModeLabel(rightMode);
    if (leftMode === rightMode) {
      return `Both sides use ${leftLabel}.`;
    }
    return `Left: ${leftLabel}. Right: ${rightLabel}.`;
  }

  private getValidationModeLabel(mode: ValidationMappingMode): string {
    if (mode === 'rules') {
      return 'pre-validation rules (JSON)';
    }
    if (mode === 'none') {
      return 'no validation mapping';
    }
    return 'legacy validation mapping (XML)';
  }

  toggleSection(key: CompareSectionKey): void {
    const section = this.sections[key];
    section.expanded = !section.expanded;
    if (!section.expanded) {
      section.showEditor = false;
      if (key === 'lookups') {
        this.clearLookupSelections(section);
        this.lookupUpdatePreview = null;
      }
      if (this.hasDiffEditor(key)) {
        this.clearDiffEditor(key as CompareEditorKey);
        if (this.isEditableSection(key)) {
          this.cancelSectionEdit(key as CompareEditorKey);
        }
      }
    } else if (this.hasDiffEditor(key) && section.loaded && !section.loading) {
      this.scheduleEditorMount(key as CompareEditorKey);
    }
    if (section.expanded && !section.loaded && !section.loading) {
      this.loadSection(key);
    }
    this.cdr.markForCheck();
  }

  /** Exit edit mode when section is collapsed (unsaved edits discarded for display). */
  private clearDiffEditor(key: CompareEditorKey): void {
    delete this.diffEditors[key];
  }

  private cancelSectionEdit(key: CompareEditorKey): void {
    const section = this.sections[key];
    if (!section.editing) {
      this.setSectionEditing(key, false);
      return;
    }
    section.editing = false;
    this.setSectionEditing(key, false);
    if (section.loaded) {
      section.leftContent = section.leftBackup;
      section.rightContent = section.rightBackup;
      this.applyDiffModels(key, section.leftContent, section.rightContent);
    }
  }

  cancelEditSection(key: CompareEditorKey): void {
    const section = this.sections[key];
    if (!section.editing || section.saving) {
      return;
    }
    this.cancelSectionEdit(key);
    if (section.expanded && section.loaded) {
      this.scheduleEditorMount(key);
    }
    this.cdr.markForCheck();
  }

  loadSection(key: CompareSectionKey): void {
    const section = this.sections[key];
    if (section.loading) {
      return;
    }
    section.loading = true;
    section.error = null;
    this.cdr.markForCheck();

    if (key === 'lookups') {
      this.loadLookupsSection(section);
      return;
    }

    const fetch$ = this.getMappingFetcher(key);

    forkJoin({
      left: fetch$(this.leftInterface).pipe(catchError((err) => of({ error: this.errorMessage(err) }))),
      right: fetch$(this.rightInterface).pipe(catchError((err) => of({ error: this.errorMessage(err) }))),
    }).subscribe({
      next: (result) => {
        section.loading = false;

        const leftErr = this.isMappingLoadError(result.left) ? result.left.error : null;
        const rightErr = this.isMappingLoadError(result.right) ? result.right.error : null;
        if (leftErr || rightErr) {
          section.error = [leftErr, rightErr].filter(Boolean).join(' | ');
        }

        if (!leftErr && this.isMappingLoadResult(result.left)) {
          const left = result.left;
          section.leftContent = left.displayXml;
          section.leftContentRaw = left.rawXml;
          section.leftBackup = left.displayXml;
          section.leftSaveMeta = left.meta;
          if (key === 'validation') {
            section.leftValidationMode = left.validationMode ?? 'legacy';
          }
        } else {
          section.leftContent = '';
          section.leftContentRaw = '';
          section.leftBackup = '';
          section.leftSaveMeta = null;
          if (key === 'validation') {
            section.leftValidationMode = 'none';
          }
        }

        if (!rightErr && this.isMappingLoadResult(result.right)) {
          const right = result.right;
          section.rightContent = right.displayXml;
          section.rightContentRaw = right.rawXml;
          section.rightBackup = right.displayXml;
          section.rightSaveMeta = right.meta;
          if (key === 'validation') {
            section.rightValidationMode = right.validationMode ?? 'legacy';
          }
        } else {
          section.rightContent = '';
          section.rightContentRaw = '';
          section.rightBackup = '';
          section.rightSaveMeta = null;
          if (key === 'validation') {
            section.rightValidationMode = 'none';
          }
        }

        section.loaded = true;

        this.applyDiffModels(key, section.leftContent, section.rightContent);
        this.scheduleEditorMount(key);
        this.cdr.markForCheck();
      },
      error: () => {
        section.loading = false;
        section.error = 'Unable to load comparison data.';
        this.cdr.markForCheck();
      },
    });
  }

  private loadLookupsSection(section: CompareSectionState): void {
    const leftBody = { ContextId: this.leftInterface.UniqueID, Handler: '' };
    const rightBody = { ContextId: this.rightInterface.UniqueID, Handler: '' };

    Promise.all([
      this.service.postapi('api/XMLMappings/GetByHandler', leftBody).catch(() => null),
      this.service.postapi('api/XMLMappings/GetByHandler', rightBody).catch(() => null),
    ])
      .then(([leftData, rightData]) => {
        section.leftLookups = this.normalizeLookups(leftData);
        section.rightLookups = this.normalizeLookups(rightData);
        section.loaded = true;
        section.loading = false;
        this.cdr.markForCheck();
      })
      .catch(() => {
        section.error = 'Unable to load lookups.';
        section.loading = false;
        this.cdr.markForCheck();
      });
  }

  private scheduleEditorMount(key: CompareEditorKey): void {
    const section = this.sections[key];
    section.showEditor = false;
    this.clearDiffEditor(key);
    setTimeout(() => {
      if (section.expanded && section.loaded) {
        section.showEditor = true;
        this.cdr.markForCheck();
      }
    }, 0);
  }

  private setSectionEditing(key: CompareEditorKey, editing: boolean): void {
    const options =
      key === 'transform'
        ? this.transformDiffOptions
        : key === 'validation'
          ? this.validationDiffOptions
          : key === 'delivery'
            ? this.deliveryDiffOptions
            : key === 'rule'
              ? this.ruleDiffOptions
              : this.vaRulesDiffOptions;
    const leftEditable = editing && this.isSideEditable('left');
    const rightEditable = editing && this.isSideEditable('right');
    options['readOnly'] = !leftEditable && !rightEditable;
    options['originalEditable'] = leftEditable;
    options['modifiedEditable'] = rightEditable;
  }

  private fetchTransformMapping$(iface: any): Observable<MappingLoadResult> {
    const url =
      'api/GetMappings/Get?routeid=' +
      iface.id +
      '&processorid=' +
      iface.PAID +
      '&processorapikey=' +
      iface.PAAPIKEY;
    return this.service.getapiurl(url).pipe(
      map((response) => this.buildMappingLoadResult(response as any, false)),
    );
  }

  private getMappingFetcher(
    key: 'transform' | 'validation' | 'delivery' | 'rule' | 'vaRules',
  ): (iface: any) => Observable<MappingLoadResult | string> {
    switch (key) {
      case 'transform':
        return (iface) => this.fetchTransformMapping$(iface);
      case 'validation':
        return (iface) => this.fetchValidationMapping$(iface);
      case 'delivery':
        return (iface) =>
          this.fetchRuleConfigByStage$(iface, 'Delivered').pipe(
            map((display) => ({
              displayXml: display,
              rawXml: display,
              meta: this.emptyMappingMeta(),
            })),
          );
      case 'rule':
        return (iface) =>
          this.fetchRuleConfigByStage$(iface, 'Validation').pipe(
            map((display) => ({
              displayXml: display,
              rawXml: display,
              meta: this.emptyMappingMeta(),
            })),
          );
      case 'vaRules':
        return (iface) => this.fetchVaRules$(iface);
    }
  }

  private emptyMappingMeta(): CompareMappingSaveMeta {
    return {
      template: '0',
      resultjson: '',
      version: '1',
      coreConfigVersionList: [],
      liveVersion: '',
    };
  }

  private getLiveCoreConfigFromMappingsResponse(response: any): any {
    const list = response?.CoreConfigVersionList;
    if (!Array.isArray(list)) {
      return null;
    }
    return list.find((c: any) => c?.islive === true) ?? null;
  }

  /**
   * Strict check: live processorconfig.validationconfig only (not transform mappedxml).
   * Same rule as CIIS / Create Interface toggle and modal routing.
   */
  private hasLegacyValidationConfig(response: any): boolean {
    const live = this.getLiveCoreConfigFromMappingsResponse(response);
    const vc = live?.json?.processorconfig?.validationconfig;
    if (!vc) {
      return false;
    }
    const mappedxml = (vc.mappedxml ?? '').toString().trim();
    const generatedxsl = (vc.generatedxsl ?? '').toString().trim();
    return !!(mappedxml || generatedxsl);
  }

  private buildMappingLoadResult(data: any, isValidation: boolean): MappingLoadResult {
    let rawXml: any;
    if (isValidation) {
      // Never fall back to top-level transform mappedxml for validation compare.
      const live = this.getLiveCoreConfigFromMappingsResponse(data);
      rawXml = live?.json?.processorconfig?.validationconfig?.mappedxml;
      if (rawXml == null || String(rawXml).trim() === '') {
        throw new Error('No legacy validation mapping (validationconfig) found');
      }
    } else {
      rawXml = data?.mappedxml;
      if (rawXml == null) {
        throw new Error('Template configurations not found');
      }
    }
    const raw = String(rawXml);
    return {
      displayXml: this.formatXml(raw),
      rawXml: raw,
      meta: this.extractMappingSaveMeta(data, isValidation),
    };
  }

  private extractMappingSaveMeta(data: any, isValidation: boolean): CompareMappingSaveMeta {
    const coreConfigVersionList = Array.isArray(data?.CoreConfigVersionList)
      ? data.CoreConfigVersionList
      : [];
    const liveEntry = coreConfigVersionList.find((c: any) => c.islive === true);
    const liveVersion = liveEntry?.version != null ? String(liveEntry.version) : '';
    let resultjson = '';
    if (data?.resultjson != null && data.resultjson !== '') {
      resultjson =
        typeof data.resultjson === 'string' ? data.resultjson : JSON.stringify(data.resultjson);
    }
    return {
      template: data?.template != null ? String(data.template) : '0',
      resultjson,
      version: '',
      coreConfigVersionList,
      liveVersion,
    };
  }

  /**
   * Mirrors xmlmappings savemappeddataDB (1356–1369). Compare has no Push to QC / Approve —
   * save always ends in Dev (100); API resets QC/Live to Dev on update, or creates next version when live.
   */
  private resolveVersionForSave(meta: CompareMappingSaveMeta | null, isValidation: boolean): string {
    if (!meta?.coreConfigVersionList?.length) {
      return meta?.version || '1';
    }
    const versionDetails = meta.coreConfigVersionList;
    const lastVersion = String(versionDetails[versionDetails.length - 1]?.version ?? '');
    const liveVersion = meta.liveVersion?.trim() ?? '';

    if (isValidation) {
      return liveVersion || lastVersion || '1';
    }

    if (liveVersion) {
      if (parseInt(lastVersion, 10) > parseInt(liveVersion, 10)) {
        return lastVersion;
      }
      return '';
    }

    return lastVersion || '1';
  }

  private applyDiffModels(
    key: CompareEditorKey,
    left: string,
    right: string,
  ): void {
    if (key === 'transform') {
      this.transformOldModel = { code: left, language: 'xml' };
      this.transformNewModel = { code: right, language: 'xml' };
    } else if (key === 'validation') {
      const validationSection = this.sections.validation;
      this.validationOldModel = {
        code: left,
        language: validationSection.leftValidationMode === 'rules' ? 'json' : 'xml',
      };
      this.validationNewModel = {
        code: right,
        language: validationSection.rightValidationMode === 'rules' ? 'json' : 'xml',
      };
    } else if (key === 'delivery') {
      this.deliveryOldModel = { code: left, language: 'json' };
      this.deliveryNewModel = { code: right, language: 'json' };
    } else if (key === 'rule') {
      this.ruleOldModel = { code: left, language: 'json' };
      this.ruleNewModel = { code: right, language: 'json' };
    } else {
      this.vaRulesOldModel = { code: left, language: 'json' };
      this.vaRulesNewModel = { code: right, language: 'json' };
    }
  }

  startEditSection(key: CompareEditorKey): void {
    const section = this.sections[key];
    if (!this.canEditSection(key) || section.loading || section.saving) {
      return;
    }
    section.editing = true;
    section.leftBackup = section.leftContent;
    section.rightBackup = section.rightContent;
    section.showEditor = false;
    this.setSectionEditing(key, true);
    this.applyDiffModels(key, section.leftContent, section.rightContent);
    setTimeout(() => {
      if (section.editing) {
        section.showEditor = true;
        this.cdr.markForCheck();
      }
    }, 0);
    this.cdr.markForCheck();
  }

  saveSection(key: CompareEditorKey): void {
    const section = this.sections[key];
    if (!section.editing || section.saving) {
      return;
    }
    const panes = this.getBothEditorContent(key);
    const leftChanged = this.contentChanged(panes.left, section.leftBackup);
    const rightChanged = this.contentChanged(panes.right, section.rightBackup);

    if (!leftChanged && !rightChanged) {
      this.toastr.info('No changes to save.', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    if (key === 'transform') {
      this.saveXmlSection(key, panes, leftChanged, rightChanged);
      return;
    }
    if (key === 'validation') {
      this.saveValidationSection(panes, leftChanged, rightChanged);
      return;
    }
    if (key === 'delivery' || key === 'rule') {
      const stage = key === 'delivery' ? 'Delivered' : 'Validation';
      this.saveRuleSection(key, panes, leftChanged, rightChanged, stage);
    }
  }

  private saveXmlSection(
    key: 'transform',
    panes: { left: string; right: string },
    leftChanged: boolean,
    rightChanged: boolean,
  ): void {
    const section = this.sections[key];
    const saves: Observable<XmlMappingSaveResult>[] = [];

    if (leftChanged) {
      if (!this.isSideEditable('left')) {
        this.toastr.warning('Left interface is locked. Left changes were not saved.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      } else {
        saves.push(
          this.saveXmlForSide('left', panes.left, this.leftInterface, section.leftSaveMeta, false, key),
        );
      }
    }

    if (rightChanged) {
      if (!this.isSideEditable('right')) {
        this.toastr.warning('Right interface is locked. Right changes were not saved.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      } else {
        saves.push(
          this.saveXmlForSide('right', panes.right, this.rightInterface, section.rightSaveMeta, false, key),
        );
      }
    }

    if (!saves.length) {
      return;
    }

    section.saving = true;
    this.cdr.markForCheck();

    forkJoin(saves).subscribe({
      next: (results) => {
        section.saving = false;
        results.forEach((r) => {
          this.applySavedPane(section, key, r.side, panes[r.side]);
          this.applySavedMeta(section, r.side, r.meta);
        });
        section.editing = false;
        this.setSectionEditing(key, false);
        this.applyDiffModels(key, section.leftContent, section.rightContent);
        this.showSaveSuccessToasts(results, key);
        this.cdr.markForCheck();
      },
      error: (err) => {
        section.saving = false;
        this.cdr.markForCheck();
        this.service.copyableToastError(err?.message || 'Unable to save mapping.');
      },
    });
  }

  /** Validation compare may mix legacy XML (core config) and pre-validation rules (RouteRuleMapping). */
  private saveValidationSection(
    panes: { left: string; right: string },
    leftChanged: boolean,
    rightChanged: boolean,
  ): void {
    const section = this.sections.validation;
    const saves: Observable<ValidationSaveResult>[] = [];

    if (leftChanged) {
      saves.push(...this.buildValidationPaneSaves('left', panes.left, section.leftValidationMode));
    }
    if (rightChanged) {
      saves.push(...this.buildValidationPaneSaves('right', panes.right, section.rightValidationMode));
    }

    if (!saves.length) {
      return;
    }

    section.saving = true;
    this.cdr.markForCheck();

    forkJoin(saves).subscribe({
      next: (results) => {
        section.saving = false;
        results.forEach((result) => {
          const content =
            'refreshedContent' in result && result.refreshedContent != null
              ? result.refreshedContent
              : panes[result.side];
          this.applySavedPane(section, 'validation', result.side, content);
          if ('meta' in result && result.meta) {
            this.applySavedMeta(section, result.side, result.meta);
          }
        });
        section.editing = false;
        this.setSectionEditing('validation', false);
        this.applyDiffModels('validation', section.leftContent, section.rightContent);
        this.showSaveSuccessToasts(results, 'validation');
        this.cdr.markForCheck();
      },
      error: (err) => {
        section.saving = false;
        this.cdr.markForCheck();
        this.service.copyableToastError(err?.message || 'Unable to save validation mapping.');
      },
    });
  }

  private buildValidationPaneSaves(
    side: ComparePaneSide,
    content: string,
    mode: ValidationMappingMode,
  ): Observable<ValidationSaveResult>[] {
    if (!this.isSideEditable(side)) {
      this.toastr.warning(
        `${side === 'left' ? 'Left' : 'Right'} interface is locked. Changes were not saved.`,
        '',
        { timeOut: 4000, positionClass: 'toast-bottom-right' },
      );
      return [];
    }

    const formatError = this.validateValidationPaneContent(side, content, mode);
    if (formatError) {
      return [throwError(() => new Error(formatError))];
    }

    const section = this.sections.validation;
    const iface = side === 'left' ? this.leftInterface : this.rightInterface;
    const meta = side === 'left' ? section.leftSaveMeta : section.rightSaveMeta;

    if (mode === 'rules') {
      return [
        this.saveRuleForSide(side, content, iface, 'PreValidation', 'validation').pipe(
          switchMap((saveResult) =>
            this.fetchRuleConfigByStage$(iface, 'PreValidation').pipe(
              map((json) => ({
                side,
                message: saveResult.message,
                refreshedContent: json,
              })),
            ),
          ),
        ),
      ];
    }

    if (mode === 'none') {
      return [
        throwError(
          () =>
            new Error(
              `${side === 'left' ? 'Left' : 'Right'} interface has no pre-validation rules or legacy validation mapping to save.`,
            ),
        ),
      ];
    }

    return [
      this.saveXmlForSide(side, content, iface, meta, true, 'validation'),
    ];
  }

  private refreshMappingSaveMeta$(
    iface: any,
    isValidation: boolean,
    fallbackMeta: CompareMappingSaveMeta | null,
  ): Observable<CompareMappingSaveMeta> {
    const fetch$ = isValidation
      ? this.fetchLegacyValidationMapping$(iface)
      : this.fetchTransformMapping$(iface);
    return fetch$.pipe(
      map((result) => result.meta),
      catchError(() => of(fallbackMeta ?? this.emptyMappingMeta())),
    );
  }

  private saveXmlForSide(
    side: ComparePaneSide,
    xml: string,
    iface: any,
    meta: CompareMappingSaveMeta | null,
    isValidation: boolean,
    sectionKey: 'transform' | 'validation',
  ): Observable<XmlMappingSaveResult> {
    const xmlToSave = (xml || '').trim();
    if (!xmlToSave) {
      return throwError(() => new Error(`${side === 'left' ? 'Left' : 'Right'} pane is empty.`));
    }
    const versionToUpdate = this.resolveVersionForSave(meta, isValidation);
    const templateId =
      meta?.template || iface?.TemplateID || iface?.templateid || '0';
    const apiUrl = isValidation
      ? 'api/CoreConfig/SaveValidationConfig'
      : 'api/CoreConfig/SaveTransformConfig';

    return this.service.XmlModification({ xml: xmlToSave, operation: 'delete' }).pipe(
      switchMap((convertResp: any) => {
        if (convertResp?.statusmessage != null) {
          return throwError(() => new Error(convertResp.statusmessage));
        }
        const payload = {
          mappedxml: convertResp?.responsebody ?? xmlToSave,
          routeid: iface?.id,
          routeuniqueid: Number(iface?.UniqueID || 0),
          processorid: iface?.PAID,
          processorapikey: iface?.PAAPIKEY,
          createdby: this.getCurrentUserId(),
          isupdateconfig: true,
          template: templateId,
          resultjson: meta?.resultjson ?? '',
          version: versionToUpdate,
          templatexml: '',
        };
        return this.service.savemappings(payload, apiUrl).pipe(
          switchMap((response) => {
            const parsed = this.parseSaveMappingsResponse(response);
            if (parsed.isFailure) {
              return throwError(() => new Error(parsed.message));
            }
            const mappingAudit = this.buildMappingSaveAudit(meta, versionToUpdate, sectionKey, side);
            this.recordPipelineSaveAudit(
              iface?.UniqueID,
              mappingAudit.actionType,
              mappingAudit.newValue,
              mappingAudit.description,
            );
            return this.refreshMappingSaveMeta$(iface, isValidation, meta).pipe(
              map((refreshedMeta) => ({
                side,
                message: parsed.message,
                meta: refreshedMeta,
              })),
            );
          }),
        );
      }),
    );
  }

  private parseSaveMappingsResponse(response: any): { isFailure: boolean; message: string } {
    try {
      const parsed = typeof response === 'string' ? JSON.parse(response) : response;
      const status = String(parsed?.status ?? '').toLowerCase();
      const message = parsed?.statusmessage || parsed?.StatusMessage || '';
      if (status === 'failure') {
        return { isFailure: true, message: message || 'Save failed.' };
      }
      return { isFailure: false, message: message || 'Saved successfully.' };
    } catch {
      return { isFailure: false, message: 'Saved successfully.' };
    }
  }

  private saveRuleSection(
    key: 'delivery' | 'rule',
    panes: { left: string; right: string },
    leftChanged: boolean,
    rightChanged: boolean,
    stage: RuleExecutionStage,
  ): void {
    const section = this.sections[key];
    const saves: Observable<{ side: ComparePaneSide; message: string }>[] = [];

    if (leftChanged) {
      if (!this.isSideEditable('left')) {
        this.toastr.warning('Left interface is locked. Left changes were not saved.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      } else {
        saves.push(this.saveRuleForSide('left', panes.left, this.leftInterface, stage, key));
      }
    }

    if (rightChanged) {
      if (!this.isSideEditable('right')) {
        this.toastr.warning('Right interface is locked. Right changes were not saved.', '', {
          timeOut: 4000,
          positionClass: 'toast-bottom-right',
        });
      } else {
        saves.push(this.saveRuleForSide('right', panes.right, this.rightInterface, stage, key));
      }
    }

    if (!saves.length) {
      return;
    }

    section.saving = true;
    this.cdr.markForCheck();

    forkJoin(saves).subscribe({
      next: (results) => {
        section.saving = false;
        results.forEach((r) => this.applySavedPane(section, key, r.side, panes[r.side]));
        section.editing = false;
        this.setSectionEditing(key, false);
        this.applyDiffModels(key, section.leftContent, section.rightContent);
        this.showSaveSuccessToasts(results, key);
        this.cdr.markForCheck();
      },
      error: (err) => {
        section.saving = false;
        this.cdr.markForCheck();
        this.service.copyableToastError(err?.message || 'Unable to save rules.');
      },
    });
  }

  /**
   * Mirrors rule-config-component onClickSave (874–901).
   * Pre-Delivery: QC saves as Dev on the same version (compare has no Push to QC).
   */
  private resolveRuleVersionForSave(
    versions: any[],
    options?: { promoteQcToDev?: boolean },
  ): {
    targetVersion: number;
    targetStatus: string;
    isSameStatusUpdate: boolean;
    isNewRuleConfigVersion: boolean;
  } {
    const promoteQcToDev = options?.promoteQcToDev === true;
    const hasVersions = versions.length > 0;
    let targetVersion = 1;
    let targetStatus = 'Dev';
    let isSameStatusUpdate = false;
    let previousLatestNumericVersion = 0;

    if (hasVersions) {
      const latest = versions[versions.length - 1];
      const currentVersion = parseInt(String(latest?.Version ?? latest?.version ?? ''), 10) || 0;
      previousLatestNumericVersion = currentVersion || 0;
      const status = String(latest?.VersionStatus ?? latest?.versionStatus ?? 'Dev')
        .trim()
        .toLowerCase();

      if (status === 'dev' || status === 'qc') {
        targetVersion = currentVersion || 1;
        if (promoteQcToDev && status === 'qc') {
          targetStatus = 'Dev';
        } else {
          targetStatus =
            String(latest?.VersionStatus ?? latest?.versionStatus ?? 'Dev').trim() || 'Dev';
        }
        isSameStatusUpdate = true;
      } else if (status === 'live') {
        targetVersion = (currentVersion || 0) + 1;
        targetStatus = 'Dev';
      } else {
        targetVersion = currentVersion || 1;
        targetStatus = 'Dev';
      }
    }

    const isNewRuleConfigVersion =
      !isSameStatusUpdate &&
      (!hasVersions || targetVersion > previousLatestNumericVersion);

    return {
      targetVersion,
      targetStatus,
      isSameStatusUpdate,
      isNewRuleConfigVersion,
    };
  }

  private saveRuleForSide(
    side: ComparePaneSide,
    jsonText: string,
    iface: any,
    stage: RuleExecutionStage,
    sectionKey: CompareEditorKey,
  ): Observable<{ side: ComparePaneSide; message: string }> {
    let parsed: any;
    try {
      parsed = JSON.parse((jsonText || '[]').trim() || '[]');
    } catch {
      return throwError(
        () => new Error(`${side === 'left' ? 'Left' : 'Right'} rule JSON is invalid.`),
      );
    }

    return this.ruleEngineService.GetRuleConfigVersions(iface?.UniqueID, stage).pipe(
      switchMap((versionsRaw) => {
        const versions = Array.isArray(versionsRaw) ? versionsRaw : [];
        const resolved = this.resolveRuleVersionForSave(versions, {
          promoteQcToDev: sectionKey === 'delivery',
        });
        const { targetVersion, targetStatus, isNewRuleConfigVersion } = resolved;

        const body = {
          RouteUniqueId: iface?.UniqueID,
          RuleConfig: JSON.stringify(parsed),
          RuleExecutionStage: stage,
          Version: targetVersion,
          VersionStatus: targetStatus,
          IsLive: false,
        };
        return this.ruleEngineService.InsertAndUpdateRuleConfig(body).pipe(
          map(() => {
            const ruleAudit = this.buildRuleSaveAudit(
              stage,
              sectionKey,
              side,
              targetVersion,
              targetStatus,
              isNewRuleConfigVersion,
            );
            this.recordPipelineSaveAudit(
              iface?.UniqueID,
              ruleAudit.actionType,
              ruleAudit.newValue,
              ruleAudit.description,
            );
            return { side, message: 'Saved successfully.' };
          }),
        );
      }),
    );
  }

  private applySavedPane(
    section: CompareSectionState,
    key: CompareEditorKey,
    side: ComparePaneSide,
    content: string,
  ): void {
    const normalized = content ?? '';
    if (side === 'left') {
      section.leftContent = normalized;
      section.leftBackup = normalized;
    } else {
      section.rightContent = normalized;
      section.rightBackup = normalized;
    }
  }

  private applySavedMeta(
    section: CompareSectionState,
    side: ComparePaneSide,
    meta: CompareMappingSaveMeta,
  ): void {
    if (side === 'left') {
      section.leftSaveMeta = meta;
    } else {
      section.rightSaveMeta = meta;
    }
  }

  private contentChanged(current: string, backup: string): boolean {
    return (current ?? '').trim() !== (backup ?? '').trim();
  }

  /** Read live left (original) and right (modified) panes from Monaco. */
  private getBothEditorContent(key: CompareEditorKey): { left: string; right: string } {
    const editor = this.diffEditors[key];
    const diffModel = editor?.getModel?.();
    const left =
      diffModel?.original?.getValue?.() ?? this.getFallbackModelCode(key, 'left');
    const right =
      diffModel?.modified?.getValue?.() ?? this.getFallbackModelCode(key, 'right');
    return { left, right };
  }

  private getFallbackModelCode(key: CompareEditorKey, side: ComparePaneSide): string {
    if (key === 'transform') {
      return side === 'left' ? this.transformOldModel.code : this.transformNewModel.code;
    }
    if (key === 'validation') {
      return side === 'left' ? this.validationOldModel.code : this.validationNewModel.code;
    }
    if (key === 'delivery') {
      return side === 'left' ? this.deliveryOldModel.code : this.deliveryNewModel.code;
    }
    if (key === 'rule') {
      return side === 'left' ? this.ruleOldModel.code : this.ruleNewModel.code;
    }
    return side === 'left' ? this.vaRulesOldModel.code : this.vaRulesNewModel.code;
  }

  private getCurrentUserId(): string {
    try {
      return JSON.parse(sessionStorage.getItem('sessionObjectlogin') || '{}')?.userdata?.userid || '';
    } catch {
      return '';
    }
  }

  /** Pipeline rules (Create Rule button) and delivery rules share the same version API. */
  private fetchRuleConfigByStage$(iface: any, ruleExecutionStage: string) {
    return this.ruleEngineService.GetRuleConfigVersions(iface.UniqueID, ruleExecutionStage).pipe(
      map((raw) => {
        const list = Array.isArray(raw) ? raw : [];
        const ruleData = this.extractRuleConfigFromVersions(list);
        return JSON.stringify(ruleData ?? [], null, 2);
      }),
    );
  }

  /** Same extraction as InterfaceComponent.extractRuleConfigFromVersions / openDeliveryMapping. */
  private extractRuleConfigFromVersions(list: any[]): any[] {
    if (!list?.length) {
      return [];
    }
    const latest = list[list.length - 1];
    const configData = latest?.RuleConfig ?? latest?.ruleConfig;
    if (!configData) {
      return [];
    }
    const savedRuleConfig = this.parseRuleConfig(configData);
    if (!savedRuleConfig) {
      return [];
    }
    return Array.isArray(savedRuleConfig) ? savedRuleConfig : [savedRuleConfig];
  }

  private parseRuleConfig(configData: any): any {
    try {
      if (typeof configData === 'string') {
        configData = JSON.parse(configData);
      }
      if (configData && typeof configData === 'object' && configData['RuleConfig']) {
        const nestedConfig = configData['RuleConfig'];
        return typeof nestedConfig === 'string' ? JSON.parse(nestedConfig) : nestedConfig;
      }
      return configData;
    } catch {
      return null;
    }
  }

  /** VA rules for pipeline — same API as VarulesComponent.getvarules. */
  private fetchVaRules$(iface: any): Observable<MappingLoadResult> {
    const ruleName = encodeURIComponent(this.resolveVaRuleName(iface));
    const deploymentKey = this.resolveVaRulesDeploymentKey(iface);
    if (!ruleName) {
      return throwError(() => new Error('Pipeline display name is missing.'));
    }
    if (!deploymentKey) {
      return throwError(() => new Error('Deployment key is missing for VA rules.'));
    }
    const apiname = `api/VitalHubDXAPI/getvarule?RuleName=${ruleName}`;
    return this.w700Service.getapi(apiname, deploymentKey).pipe(
      map((data) => {
        const json = this.formatVaRulesJson(data);
        return {
          displayXml: json,
          rawXml: json,
          meta: this.emptyMappingMeta(),
        };
      }),
    );
  }

  private resolveVaRuleName(iface: any): string {
    for (const key of ['displayname', 'DisplayName']) {
      const val = iface?.[key];
      if (val !== undefined && val !== null && String(val).trim() !== '') {
        return String(val).trim();
      }
    }
    return '';
  }

  /** Inbound → target deploy key; outbound → source (create / interface openvarulepopup). */
  private resolveVaRulesDeploymentKey(iface: any): string {
    const direction = String(iface?.Direction ?? iface?.direction ?? '').toLowerCase();
    if (direction.includes('inbound')) {
      return String(
        iface?.DEDEPLOYMENTKEY ??
          iface?.dedeploymentkey ??
          iface?.targetdeploy ??
          '',
      ).trim();
    }
    return String(
      iface?.SEDEPLOYMENTKEY ??
        iface?.sedeploymentkey ??
        iface?.sourcedeploy ??
        '',
    ).trim();
  }

  private formatVaRulesJson(data: any): string {
    const list = Array.isArray(data) ? data : data != null ? [data] : [];
    try {
      return JSON.stringify(list, null, 2);
    } catch {
      return '[]';
    }
  }

  private fetchValidationMapping$(iface: any): Observable<MappingLoadResult> {
    const uniqueId = iface?.UniqueID;
    if (!uniqueId) {
      return this.fetchLegacyValidationMapping$(iface);
    }
    return this.ruleEngineService.GetRuleConfigVersions(uniqueId, 'PreValidation').pipe(
      switchMap((raw) => {
        const list = Array.isArray(raw) ? raw : [];
        if (list.length > 0) {
          return this.fetchRuleConfigByStage$(iface, 'PreValidation').pipe(
            map((json) => this.buildValidationRulesLoadResult(json)),
          );
        }
        return this.fetchLegacyValidationMapping$(iface);
      }),
    );
  }

  /** Legacy validationconfig from GetMappings (prevalidation=true). Strict: no transform mappedxml. */
  private fetchLegacyValidationMapping$(iface: any): Observable<MappingLoadResult> {
    const url =
      'api/GetMappings/Get?routeid=' +
      iface.id +
      '&processorid=' +
      iface.PAID +
      '&processorapikey=' +
      iface.PAAPIKEY +
      '&prevalidation=true';
    return this.service.getapiurl(url).pipe(
      map((response) => {
        if (!this.hasLegacyValidationConfig(response)) {
          return this.emptyValidationMappingResult(iface);
        }
        const live = this.getLiveCoreConfigFromMappingsResponse(response);
        const mappedXml = live?.json?.processorconfig?.validationconfig?.mappedxml;
        if (mappedXml == null || String(mappedXml).trim() === '') {
          // generatedxsl-only: treat as no displayable validation mapping for compare.
          return this.emptyValidationMappingResult(iface);
        }
        const result = this.buildMappingLoadResult(response as unknown as Record<string, unknown>, true);
        return { ...result, validationMode: 'legacy' as ValidationMappingMode };
      }),
    );
  }

  private emptyValidationMappingResult(iface: any): MappingLoadResult {
    const name = this.resolveVaRuleName(iface) || 'Interface';
    this.toastr.warning(
      `${name}: no pre-validation rules or legacy validation mapping found.`,
      '',
      { timeOut: 4000, positionClass: 'toast-bottom-right' },
    );
    return {
      displayXml: '',
      rawXml: '',
      meta: this.emptyMappingMeta(),
      validationMode: 'none',
    };
  }

  private buildValidationRulesLoadResult(json: string): MappingLoadResult {
    return {
      displayXml: json,
      rawXml: json,
      meta: this.emptyMappingMeta(),
      validationMode: 'rules',
    };
  }

  /** Light-red diff: value mismatch (same type+id) or row missing on other side. */
  isLookupRowDifferent(row: CompareLookupRow, side: ComparePaneSide): boolean {
    const section = this.sections.lookups;
    const otherRows =
      side === 'left' ? section.rightLookups : section.leftLookups;
    const otherIface = side === 'left' ? this.rightInterface : this.leftInterface;
    const match = this.findLookupCounterpartForDiff(
      otherRows,
      row.lookupType,
      row.lookupId,
      otherIface?.UniqueID,
    );
    if (!match) {
      return true;
    }
    return (
      String(row.lookupValue ?? '').trim() !==
      String(match.lookupValue ?? '').trim()
    );
  }

  /** Selected rows first for display; underlying arrays unchanged for copy logic. */
  getDisplayLookups(side: ComparePaneSide): CompareLookupRow[] {
    const section = this.sections.lookups;
    const rows =
      side === 'left' ? section.leftLookups : section.rightLookups;
    const selected = rows.filter((r) => r.selected);
    const unselected = rows.filter((r) => !r.selected);
    return [...selected, ...unselected];
  }

  hasLookupSelection(): boolean {
    const section = this.sections.lookups;
    return (
      section.leftLookups.some((r) => r.selected) ||
      section.rightLookups.some((r) => r.selected)
    );
  }

  cancelLookupSelection(): void {
    this.clearLookupSelections(this.sections.lookups);
    this.lookupUpdatePreview = null;
    this.cdr.markForCheck();
  }

  isLookupCheckboxDisabled(_side: ComparePaneSide): boolean {
    const section = this.sections.lookups;
    return section.lookupSyncing || section.loading || !!this.lookupUpdatePreview;
  }

  onLookupRowSelectionChange(side: ComparePaneSide, row: CompareLookupRow, checked: boolean): void {
    if (this.isLookupCheckboxDisabled(side)) {
      return;
    }
    row.selected = checked;
    this.cdr.markForCheck();
  }

  canCopyLookupsToRight(): boolean {
    const section = this.sections.lookups;
    return (
      section.leftLookups.some((r) => r.selected) &&
      !section.lookupSyncing &&
      !section.loading &&
      !this.lookupUpdatePreview &&
      !this.isInterfaceLocked(this.rightInterface)
    );
  }

  canCopyLookupsToLeft(): boolean {
    const section = this.sections.lookups;
    return (
      section.rightLookups.some((r) => r.selected) &&
      !section.lookupSyncing &&
      !section.loading &&
      !this.lookupUpdatePreview &&
      !this.isInterfaceLocked(this.leftInterface)
    );
  }

  copyLookupsToRight(): void {
    this.copyLookupsBetweenSides('left', 'right');
  }

  copyLookupsToLeft(): void {
    this.copyLookupsBetweenSides('right', 'left');
  }

  cancelLookupUpdatePreview(): void {
    this.lookupUpdatePreview = null;
    this.cdr.markForCheck();
  }

  confirmLookupUpdatePreview(): void {
    const preview = this.lookupUpdatePreview;
    if (!preview) {
      return;
    }
    this.lookupUpdatePreview = null;
    void this.executeLookupUpdates(preview.sourceSide, preview.targetSide, preview.pairs);
  }

  formatLookupRowLabel(row: CompareLookupRow): string {
    return `${row.lookupType} | ${row.lookupId} | ${row.lookupValue}`;
  }

  /** Selected rows in display order (selected block at top). */
  private getSelectedLookupsInDisplayOrder(side: ComparePaneSide): CompareLookupRow[] {
    return this.getDisplayLookups(side).filter((r) => r.selected);
  }

  private copyLookupsBetweenSides(
    sourceSide: ComparePaneSide,
    targetSide: ComparePaneSide,
  ): void {
    const section = this.sections.lookups;
    const targetIface = targetSide === 'left' ? this.leftInterface : this.rightInterface;
    const sourceSelected = this.getSelectedLookupsInDisplayOrder(sourceSide);
    const targetSelected = this.getSelectedLookupsInDisplayOrder(targetSide);

    if (!sourceSelected.length) {
      this.toastr.warning('Select at least one lookup row to copy.', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }
    if (this.isInterfaceLocked(targetIface)) {
      this.toastr.warning(
        `${targetSide === 'left' ? 'Left' : 'Right'} interface is locked.`,
        '',
        { timeOut: 4000, positionClass: 'toast-bottom-right' },
      );
      return;
    }

    // Both sides selected → update by position (equal count required).
    if (targetSelected.length > 0) {
      if (sourceSelected.length !== targetSelected.length) {
        this.toastr.warning(
          'Equal number of rows needs to be selected to update.',
          '',
          { timeOut: 4500, positionClass: 'toast-bottom-right' },
        );
        return;
      }
      this.lookupUpdatePreview = {
        sourceSide,
        targetSide,
        pairs: sourceSelected.map((source, i) => ({
          source,
          target: targetSelected[i],
        })),
      };
      this.cdr.markForCheck();
      return;
    }

    // Source only → insert
    void this.executeLookupInserts(sourceSide, targetSide, sourceSelected);
  }

  private async executeLookupInserts(
    sourceSide: ComparePaneSide,
    targetSide: ComparePaneSide,
    sourceSelected: CompareLookupRow[],
  ): Promise<void> {
    const section = this.sections.lookups;
    const targetRows =
      targetSide === 'left' ? section.leftLookups : section.rightLookups;
    const targetIface = targetSide === 'left' ? this.leftInterface : this.rightInterface;
    const targetUniqueId = targetIface?.UniqueID;

    section.lookupSyncing = true;
    this.cdr.markForCheck();

    let addedCount = 0;
    let skippedCount = 0;
    const failures: string[] = [];

    for (const sourceRow of sourceSelected) {
      if (!this.isLookupRowComplete(sourceRow)) {
        failures.push(
          `Skipped incomplete row (${sourceRow.lookupType}/${sourceRow.lookupId}).`,
        );
        continue;
      }

      if (
        this.findLookupByFullKey(
          targetRows,
          sourceRow.lookupType,
          sourceRow.lookupId,
          sourceRow.lookupValue,
        )
      ) {
        skippedCount++;
        continue;
      }

      const payload = {
        id: 0,
        LookupType: sourceRow.lookupType,
        LookupId: sourceRow.lookupId,
        LookupValue: sourceRow.lookupValue,
        Handler: targetIface?.id ?? '',
      };

      try {
        const response = await this.service.postapi('api/XMLMappings/CreateUpdate', payload);
        if (response?.['status'] && response?.['statusText']) {
          failures.push(
            `${sourceRow.lookupType}/${sourceRow.lookupId}: save failed.`,
          );
          continue;
        }
        const res = response as any;
        if (res == null || String(res?.['id'] ?? res?.Id ?? '') === '-1') {
          failures.push(
            `${sourceRow.lookupType}/${sourceRow.lookupId}: already exists.`,
          );
          continue;
        }
        const savedId = Number(res?.['id'] ?? res?.Id ?? 0) || 0;
        targetRows.push({
          id: savedId,
          contextId: String(targetUniqueId ?? ''),
          lookupType: sourceRow.lookupType,
          lookupId: sourceRow.lookupId,
          lookupValue: sourceRow.lookupValue,
          selected: false,
        });
        this.sortLookupRows(targetRows);
        addedCount++;
      } catch {
        failures.push(
          `${sourceRow.lookupType}/${sourceRow.lookupId}: save failed.`,
        );
      }
    }

    await this.finishLookupSync({
      sourceSide,
      targetSide,
      targetIface,
      selectedCount: sourceSelected.length,
      updatedCount: 0,
      addedCount,
      skippedCount,
      failures,
    });
  }

  private async executeLookupUpdates(
    sourceSide: ComparePaneSide,
    targetSide: ComparePaneSide,
    pairs: Array<{ source: CompareLookupRow; target: CompareLookupRow }>,
  ): Promise<void> {
    const section = this.sections.lookups;
    const targetRows =
      targetSide === 'left' ? section.leftLookups : section.rightLookups;
    const targetIface = targetSide === 'left' ? this.leftInterface : this.rightInterface;

    section.lookupSyncing = true;
    this.cdr.markForCheck();

    let updatedCount = 0;
    let skippedCount = 0;
    const failures: string[] = [];

    for (const { source: sourceRow, target: targetRow } of pairs) {
      if (!this.isLookupRowComplete(sourceRow)) {
        failures.push(
          `Skipped incomplete row (${sourceRow.lookupType}/${sourceRow.lookupId}).`,
        );
        continue;
      }

      const sameAsTarget =
        this.lookupFullMatchKey(
          sourceRow.lookupType,
          sourceRow.lookupId,
          sourceRow.lookupValue,
        ) ===
        this.lookupFullMatchKey(
          targetRow.lookupType,
          targetRow.lookupId,
          targetRow.lookupValue,
        );
      if (sameAsTarget) {
        skippedCount++;
        continue;
      }

      const duplicateElsewhere = this.findLookupByFullKey(
        targetRows,
        sourceRow.lookupType,
        sourceRow.lookupId,
        sourceRow.lookupValue,
        targetRow.id,
      );
      if (duplicateElsewhere) {
        failures.push(
          `${sourceRow.lookupType}/${sourceRow.lookupId}/${sourceRow.lookupValue}: duplicate exists on target.`,
        );
        continue;
      }

      if (!targetRow.id) {
        failures.push(
          `${targetRow.lookupType}/${targetRow.lookupId}: cannot update row without id.`,
        );
        continue;
      }

      const payload = {
        id: targetRow.id,
        LookupType: sourceRow.lookupType,
        LookupId: sourceRow.lookupId,
        LookupValue: sourceRow.lookupValue,
        Handler: targetIface?.id ?? '',
      };

      try {
        const response = await this.service.postapi('api/XMLMappings/CreateUpdate', payload);
        if (response?.['status'] && response?.['statusText']) {
          failures.push(
            `${sourceRow.lookupType}/${sourceRow.lookupId}: save failed.`,
          );
          continue;
        }
        const res = response as any;
        if (res == null || String(res?.['id'] ?? res?.Id ?? '') === '-1') {
          failures.push(
            `${sourceRow.lookupType}/${sourceRow.lookupId}: already exists.`,
          );
          continue;
        }
        targetRow.lookupType = sourceRow.lookupType;
        targetRow.lookupId = sourceRow.lookupId;
        targetRow.lookupValue = sourceRow.lookupValue;
        updatedCount++;
      } catch {
        failures.push(
          `${sourceRow.lookupType}/${sourceRow.lookupId}: save failed.`,
        );
      }
    }

    await this.finishLookupSync({
      sourceSide,
      targetSide,
      targetIface,
      selectedCount: pairs.length,
      updatedCount,
      addedCount: 0,
      skippedCount,
      failures,
    });
  }

  private async finishLookupSync(args: {
    sourceSide: ComparePaneSide;
    targetSide: ComparePaneSide;
    targetIface: any;
    selectedCount: number;
    updatedCount: number;
    addedCount: number;
    skippedCount: number;
    failures: string[];
  }): Promise<void> {
    const section = this.sections.lookups;
    await this.reloadAllLookups();
    this.clearLookupSelections(section);
    this.lookupUpdatePreview = null;
    section.lookupSyncing = false;

    const sourceLabel = args.sourceSide === 'left' ? 'Left' : 'Right';
    const targetLabel = args.targetSide === 'left' ? 'Left' : 'Right';
    const summary = `${args.updatedCount} updated, ${args.addedCount} added`;
    const auditMessage = `Compare ${sourceLabel} to ${targetLabel} Look ups: ${summary}${
      args.skippedCount > 0 ? `, ${args.skippedCount} skipped` : ''
    } (${args.selectedCount} selected)`;

    if (args.updatedCount > 0 || args.addedCount > 0) {
      const actionType =
        args.addedCount > 0 && args.updatedCount === 0 ? 'Create' : 'Update';
      this.recordPipelineSaveAudit(
        args.targetIface?.UniqueID,
        actionType,
        auditMessage,
        'Lookup sync completed successfully',
      );
      let successMsg = `Look ups copied to ${targetLabel}: ${summary}.`;
      if (args.skippedCount > 0) {
        successMsg += ` ${args.skippedCount} skipped (already exist on target).`;
      }
      this.toastr.success(successMsg, '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
    }

    if (args.failures.length) {
      this.service.copyableToastError(args.failures.join('\n'));
    } else if (
      args.skippedCount > 0 &&
      args.updatedCount === 0 &&
      args.addedCount === 0
    ) {
      this.toastr.info('Already exists on target — nothing to copy.', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right',
      });
    } else if (
      args.updatedCount === 0 &&
      args.addedCount === 0 &&
      args.skippedCount === 0
    ) {
      this.toastr.info('No lookups were copied.', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right',
      });
    }

    this.cdr.markForCheck();
  }

  private isLookupRowComplete(row: CompareLookupRow): boolean {
    return (
      !!String(row.lookupType ?? '').trim() &&
      !!String(row.lookupId ?? '').trim() &&
      !!String(row.lookupValue ?? '').trim()
    );
  }

  /** For diff display: prefer pipeline row on other side, then shared template, then any key match. */
  private findLookupCounterpartForDiff(
    otherRows: CompareLookupRow[],
    lookupType: string,
    lookupId: string,
    otherPipelineUniqueId: string | number | undefined,
  ): CompareLookupRow | undefined {
    return (
      this.findPipelineLookupOnSide(otherRows, lookupType, lookupId, otherPipelineUniqueId) ??
      this.findTemplateLookupOnSide(otherRows, lookupType, lookupId) ??
      this.findLookupByKey(otherRows, lookupType, lookupId)
    );
  }

  private findTemplateLookupOnSide(
    rows: CompareLookupRow[],
    lookupType: string,
    lookupId: string,
  ): CompareLookupRow | undefined {
    const key = this.lookupMatchKey(lookupType, lookupId);
    return rows.find(
      (r) =>
        String(r.contextId ?? '').trim() === '-1' &&
        this.lookupMatchKey(r.lookupType, r.lookupId) === key,
    );
  }

  private findLookupByFullKey(
    rows: CompareLookupRow[],
    lookupType: string,
    lookupId: string,
    lookupValue: string,
    excludeId?: number,
  ): CompareLookupRow | undefined {
    const key = this.lookupFullMatchKey(lookupType, lookupId, lookupValue);
    return rows.find((r) => {
      if (excludeId != null && excludeId > 0 && r.id === excludeId) {
        return false;
      }
      return (
        this.lookupFullMatchKey(r.lookupType, r.lookupId, r.lookupValue) === key
      );
    });
  }

  private findLookupByKey(
    rows: CompareLookupRow[],
    lookupType: string,
    lookupId: string,
  ): CompareLookupRow | undefined {
    const key = this.lookupMatchKey(lookupType, lookupId);
    return rows.find((r) => this.lookupMatchKey(r.lookupType, r.lookupId) === key);
  }

  /** Match only pipeline-owned rows; ignore shared template rows (ContextId = -1). */
  private findPipelineLookupOnSide(
    rows: CompareLookupRow[],
    lookupType: string,
    lookupId: string,
    pipelineUniqueId: string | number | undefined,
  ): CompareLookupRow | undefined {
    const key = this.lookupMatchKey(lookupType, lookupId);
    return rows.find(
      (r) =>
        this.isPipelineLookupRow(r, pipelineUniqueId) &&
        this.lookupMatchKey(r.lookupType, r.lookupId) === key,
    );
  }

  private isPipelineLookupRow(
    row: CompareLookupRow,
    pipelineUniqueId: string | number | undefined,
  ): boolean {
    if (pipelineUniqueId === undefined || pipelineUniqueId === null || pipelineUniqueId === '') {
      return false;
    }
    const rowContextId = String(row.contextId ?? '').trim();
    if (!rowContextId || rowContextId === '-1') {
      return false;
    }
    return this.contextIdsEqual(rowContextId, pipelineUniqueId);
  }

  private contextIdsEqual(
    a: string | number | undefined,
    b: string | number | undefined,
  ): boolean {
    const sa = String(a ?? '').trim();
    const sb = String(b ?? '').trim();
    if (!sa || !sb) {
      return false;
    }
    if (sa === sb) {
      return true;
    }
    const na = Number(sa);
    const nb = Number(sb);
    return !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
  }

  private lookupMatchKey(lookupType: string, lookupId: string): string {
    return `${String(lookupType ?? '').trim()}|${String(lookupId ?? '').trim()}`.toLowerCase();
  }

  private lookupFullMatchKey(
    lookupType: string,
    lookupId: string,
    lookupValue: string,
  ): string {
    return `${String(lookupType ?? '').trim()}|${String(lookupId ?? '').trim()}|${String(lookupValue ?? '').trim()}`.toLowerCase();
  }

  private sortLookupRows(rows: CompareLookupRow[]): void {
    rows.sort((a, b) =>
      `${a.lookupType}|${a.lookupId}`.localeCompare(`${b.lookupType}|${b.lookupId}`),
    );
  }

  private clearLookupSelections(section: CompareSectionState): void {
    section.lookupSelectionSide = null;
    section.leftLookups.forEach((r) => (r.selected = false));
    section.rightLookups.forEach((r) => (r.selected = false));
  }

  private async reloadAllLookups(): Promise<void> {
    await Promise.all([
      this.reloadLookupsForSide('left'),
      this.reloadLookupsForSide('right'),
    ]);
  }

  private async reloadLookupsForSide(side: ComparePaneSide): Promise<void> {
    const section = this.sections.lookups;
    const iface = side === 'left' ? this.leftInterface : this.rightInterface;
    const body = { ContextId: iface.UniqueID, Handler: '' };
    try {
      const data = await this.service.postapi('api/XMLMappings/GetByHandler', body);
      const rows = this.normalizeLookups(data);
      if (side === 'left') {
        section.leftLookups = rows;
      } else {
        section.rightLookups = rows;
      }
    } catch {
      // Keep existing rows on reload failure.
    }
  }

  private normalizeLookups(data: any): CompareLookupRow[] {
    const list = Array.isArray(data) ? data : [];
    return list
      .map((row) => ({
        id: Number(row.Id ?? row.id ?? 0) || 0,
        contextId: String(row.ContextId ?? row.contextId ?? ''),
        lookupType: row.LookupType || row.lookupType || '',
        lookupId: row.LookupId || row.lookupId || '',
        lookupValue: row.LookupValue || row.lookupValue || '',
        selected: false,
      }))
      .sort((a, b) =>
        `${a.lookupType}|${a.lookupId}`.localeCompare(`${b.lookupType}|${b.lookupId}`),
      );
  }

  private formatXml(xml: string): string {
    if (!xml) {
      return '';
    }
    const cleaned = this.stripNodeIdentifierFromXml(xml);
    if (cleaned.length > 250000) {
      return cleaned;
    }
    try {
      return this.stripNodeIdentifierFromXml(beautify(cleaned));
    } catch {
      return cleaned;
    }
  }

  /** Display-only: remove internal GUID attrs/elements (raw API XML kept for save). */
  private stripNodeIdentifierFromXml(xml: string): string {
    return xml
      .replace(/\s+_NodeIdentifier\s*=\s*(?:"[^"]*"|'[^']*')/gi, '')
      .replace(/_NodeIdentifier\s*=\s*(?:"[^"]*"|'[^']*')/gi, '')
      .replace(/<\s*_NodeIdentifier\b[^>]*\/>/gi, '')
      .replace(/<\s*_NodeIdentifier\b[^>]*>[\s\S]*?<\/\s*_NodeIdentifier\s*>/gi, '');
  }

  private isMappingLoadError(value: unknown): value is MappingLoadError {
    return (
      typeof value === 'object' &&
      value !== null &&
      'error' in value &&
      typeof (value as MappingLoadError).error === 'string'
    );
  }

  private isMappingLoadResult(value: unknown): value is MappingLoadResult {
    return (
      typeof value === 'object' &&
      value !== null &&
      'displayXml' in value &&
      'rawXml' in value &&
      'meta' in value
    );
  }

  private resolveRuleAuditLabel(stage: RuleExecutionStage): string {
    if (stage === 'Delivered') {
      return 'Predelivery Rule Config';
    }
    if (stage === 'PreValidation') {
      return 'PreValidation Rule Config';
    }
    return 'RuleConfig';
  }

  private validateValidationPaneContent(
    side: ComparePaneSide,
    content: string,
    mode: ValidationMappingMode,
  ): string | null {
    const sideLabel = side === 'left' ? 'Left' : 'Right';
    const trimmed = (content ?? '').trim();

    if (mode === 'rules') {
      if (this.looksLikeXml(trimmed) && !this.canParseJson(trimmed)) {
        return `${sideLabel} pane must contain pre-validation rule JSON, not XML.`;
      }
      if (!this.canParseJson(trimmed || '[]')) {
        return `${sideLabel} pane contains invalid pre-validation rule JSON.`;
      }
      return null;
    }

    if (mode === 'none') {
      return `${sideLabel} interface has no pre-validation rules or legacy validation mapping.`;
    }

    if (this.looksLikeJson(trimmed) && !this.looksLikeXml(trimmed)) {
      return `${sideLabel} pane must contain legacy validation mapping XML, not JSON.`;
    }
    return null;
  }

  private looksLikeJson(text: string): boolean {
    if (!text) {
      return false;
    }
    const first = text.charAt(0);
    return (first === '{' || first === '[') && this.canParseJson(text);
  }

  private looksLikeXml(text: string): boolean {
    if (!text) {
      return false;
    }
    const trimmed = text.trimStart();
    return trimmed.startsWith('<?xml') || trimmed.startsWith('<');
  }

  private canParseJson(text: string): boolean {
    try {
      JSON.parse(text);
      return true;
    } catch {
      return false;
    }
  }

  private errorMessage(err: unknown): string {
    if (typeof err === 'string') {
      return err;
    }
    if (err instanceof Error) {
      return err.message;
    }
    if (typeof err === 'object' && err !== null && 'message' in err) {
      const message = (err as { message?: unknown }).message;
      if (typeof message === 'string') {
        return message;
      }
    }
    return 'Load failed';
  }

  close(): void {
    this.activeModal.dismiss();
  }

  copyToClip(value: string, label: string): void {
    if (value && typeof copyToClipboard === 'function') {
      copyToClipboard(value);
      this.toastr.success(label + ' copied', '', {
        timeOut: 2000,
        positionClass: 'toast-bottom-right',
      });
    }
  }
}

declare const copyToClipboard: (content: string) => void;
